import { Card, CardContent } from '@/components/ui/card';
import { TrendingDown, TrendingUp } from 'lucide-react';

const COLOR_CLASSES = {
  blue: { bg: 'bg-info/10', text: 'text-info' },
  info: { bg: 'bg-info/10', text: 'text-info' },
  emerald: { bg: 'bg-success/10', text: 'text-success' },
  success: { bg: 'bg-success/10', text: 'text-success' },
  amber: { bg: 'bg-warning/10', text: 'text-warning' },
  warning: { bg: 'bg-warning/10', text: 'text-warning' },
  purple: { bg: 'bg-primary/10', text: 'text-primary' },
  primary: { bg: 'bg-primary/10', text: 'text-primary' },
  red: { bg: 'bg-destructive/10', text: 'text-destructive' },
  destructive: { bg: 'bg-destructive/10', text: 'text-destructive' },
  rose: { bg: 'bg-rose-50 dark:bg-rose-900/30', text: 'text-rose-600 dark:text-rose-400' },
  gray: { bg: 'bg-muted', text: 'text-muted-foreground' },
  muted: { bg: 'bg-muted', text: 'text-muted-foreground' },
};

function formatNumericKpi(value) {
  if (!Number.isFinite(value)) return '0';

  const abs = Math.abs(value);
  const maximumFractionDigits = Number.isInteger(value) ? 0 : abs >= 100 ? 0 : 1;

  return value.toLocaleString(undefined, {
    maximumFractionDigits,
  });
}

function formatKpiValue(value) {
  if (typeof value === 'number') return formatNumericKpi(value);

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^(-?\d+(?:\.\d+)?)(%)?$/);
    if (match) {
      return `${formatNumericKpi(Number(match[1]))}${match[2] || ''}`;
    }
  }

  return value;
}

export function KpiCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'blue',
  trend,
  className = '',
  valueClassName = '',
  'data-testid': dataTestId,
}) {
  const c = COLOR_CLASSES[color] || COLOR_CLASSES.blue;
  const normalizedTestId = dataTestId || `stat-${String(title).toLowerCase().replace(/\s+/g, '-')}`;
  const displayValue = formatKpiValue(value);
  const displayTrend = trend !== undefined ? formatKpiValue(`${Math.abs(trend)}%`) : null;

  return (
    <Card className={`h-full min-w-0 bg-card border-border shadow-sm transition-shadow hover:shadow-md ${className}`} data-testid={normalizedTestId}>
      <CardContent className="p-4 sm:p-5 lg:p-6">
        <div className="relative min-w-0">
          <div className={`flex min-w-0 items-start gap-3 sm:gap-4 ${trend !== undefined ? 'pr-14 sm:pr-16' : ''}`}>
            {Icon && (
              <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${c.bg} sm:h-11 sm:w-11 lg:h-12 lg:w-12`}>
                <Icon className={`h-5 w-5 ${c.text} lg:h-6 lg:w-6`} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="max-w-full whitespace-pre-line break-normal text-sm font-medium leading-snug text-muted-foreground hyphens-none">
                {title}
              </p>
              <p className={`mt-2 max-w-full break-words text-2xl font-bold leading-tight text-foreground sm:text-3xl ${valueClassName}`}>
                {displayValue}
              </p>
            </div>
          </div>
          {trend !== undefined && (
            <div className={`absolute right-0 top-0 flex items-center gap-1 whitespace-nowrap text-xs sm:text-sm ${trend >= 0 ? 'text-success' : 'text-destructive'}`}>
              {trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              {displayTrend}
            </div>
          )}
        </div>
        {subtitle && <p className="mt-3 text-xs leading-snug text-muted-foreground sm:text-sm">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
